import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setupSocket, enterBackend, socketOn } from '../../helpers';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';

import { useTranslation, Trans } from 'react-i18next';

import Header from '../../components/Header';
import CSVDrop from '../../components/CSVDrop';
import csv_template from '../../assets/simcase_csv_template.csv';
import PlayersManager from '../../components/PlayersList';

// Actions
import { gameActions } from '../../actions';

// Constants
const roleLabels = ['Candidate', 'Recruiter'];

const CSVInstructions = () => (
  <div>
    <h5 className='mb-3'>
      <Trans i18nKey='instructions'>Instructions</Trans>
    </h5>
    <ol>
      <li className='mb-3'>
        <Trans i18nKey='csv_instructions_step_1'>
          Structure your class list in a spreadsheet as follows
        </Trans>
        (click{' '}
        <b>
          <a href={csv_template} target='_blank' rel='noreferrer'>
            <Trans i18nKey='here'>here</Trans>
          </a>
        </b>{' '}
        <Trans i18nKey='download_copy'>to download a copy</Trans>):
        <table className='table table-sm table-bordered table-striped mt-3'>
          <tbody>
            <tr>
              <td>player1@email.com</td>
              <td>
                <Trans i18nKey='Name'>Name</Trans>
              </td>
              <td>
                <Trans i18nKey='Surname'>Surname</Trans>
              </td>
            </tr>
            <tr>
              <td>player2@email.com</td>
              <td>
                <Trans i18nKey='Name'>Name</Trans>
              </td>
              <td>
                <Trans i18nKey='Surname'>Surname</Trans>
              </td>
            </tr>
            <tr>
              <td>player3@email.com</td>
              <td>
                <Trans i18nKey='Name'>Name</Trans>
              </td>
              <td>
                <Trans i18nKey='Surname'>Surname</Trans>
              </td>
            </tr>
            <tr>
              <td>player4@email.com</td>
              <td>
                <Trans i18nKey='Name'>Name</Trans>
              </td>
              <td>
                <Trans i18nKey='Surname'>Surname</Trans>
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          <b>
            <Trans i18nKey='Note'>Note</Trans>
          </b>
          :{' '}
          <Trans i18nKey='roles_assignment'>
            The roles in this game are assigned in the following order:
          </Trans>
        </p>
        {/* Roles */}
        <Trans i18nKey='first_email_role'>
          The first email in the list → Candidate
        </Trans>{' '}
        <br />
        <Trans i18nKey='second_email_role'>
          The second email in the list → Recruiter
        </Trans>{' '}
        <br />
        <Trans i18nKey='third_email_role'>
          The third email in the list → Candidate
        </Trans>{' '}
        <br />
        <Trans i18nKey='fourth_email_role'>
          The fourth email in the list → Recruiter
        </Trans>
      </li>
      <li className='mb-3'>
        <Trans i18nKey='csv_instructions_step_2'>
          Copy and paste your data into the 'Registered Players' text area at
          right.
        </Trans>
      </li>
      <li className='mb-0'>
        <Trans i18nKey='csv_instructions_step_3'>
          Click 'Save', verify the emails and names, then click 'Launch'.
        </Trans>
      </li>
    </ol>
  </div>
);

// Main Component: Form
const GameForm = () => {
  // State
  const [onboarding, setOnboarding] = useState(false);
  const [item, setItem] = useState({});
  const [clickedLaunch, setClickedLaunch] = useState(false);
  const [fetching, setFetching] = useState(false);

  // Hooks
  const dispatch = useDispatch();
  const game = useSelector((state) => state.game);
  const { t } = useTranslation();

  // Effects
  useEffect(() => {
    const onboarding = localStorage?.getItem('onboarding');
    if (onboarding === 'false') return;
    setOnboarding(true);
  }, []);

  useEffect(() => {
    fetch(process.env.REACT_APP_API_URL + '/auth/oauth_provider')
      .then((res) => res.json())
      .then((data) => {
        setupSocket(data.io_domain, () => {
          if (item && item._id) {
            enterBackend({ game_id: item._id }, (gameData) => {
              console.log(`Connected to ${gameData._id} ws backend.`);
            });
            socketOn('player', () => {
              dispatch(gameActions.fetchGames(item._id));
            });
          }
        });
      });
  }, [dispatch, item]);

  // Effect: Component initialisation
  useEffect(() => {
    if (game?.data) {
      setItem(game.data);
    }
  }, [game?.data]);

  // Methods
  const handleChange = (e) => {
    const { name, value } = e.target;
    const newItem = {
      ...item,
      [name]: name === 'players_text' ? value.replace(/\t/g, ', ') : value,
      changed: true,
    };
    setItem(newItem);
    // setChanged(true);

    if (name === 'access_type') {
      dispatch(gameActions.updateGame(newItem));
    }
  };

  const submit = () => {
    dispatch(gameActions.updateGame(item));
  };

  const launch = () => {
    setClickedLaunch(true);
    dispatch(gameActions.launchGame(item));
  };

  const readCSV = (content) => {
    const newItem = { ...item, players_text: content };
    dispatch(gameActions.updateGame(newItem));
  };

  const dississOnboarding = () => {
    setOnboarding(false);
    localStorage?.setItem('onboarding', false);
  };

  // Validation
  if (!item) return <div>{t('Loading...')}</div>;
  if (item?.status === 'launched' && clickedLaunch)
    return <Navigate to={'/games/' + item?._id + '/players'} />;

  // Logic

  // Helper function:
  // If no gindex is provided, find the group the player belongs to
  // and get the index of that group, use that index+1 as gindex
  const fillGindexFromGroups = (p, groupsArray) => {
    if (typeof p.gindex === 'undefined') {
      const groupIndex = groupsArray.findIndex((g) =>
        g.players.find((gp) => gp.email === p.email)
      );
      p.gindex = groupIndex !== -1 ? groupIndex + 1 : null;
    }
    return p;
  };

  // Helper function:
  // If no rindex is provided on the registered player,
  // find the player and get the rindex from the player element
  const fillRindexFromPlayers = (p, playersArray) => {
    if (typeof p.rindex === 'undefined') {
      const playerIndex = playersArray.findIndex((gp) => gp.email === p.email);
      p.rindex = playerIndex !== -1 ? playersArray[playerIndex].rindex : null;
    }
    return p;
  };

  const registeredPlayers = item?.registered
    ?.map((r) => fillGindexFromGroups(r, item?.groups)) // Fill gindex if not set
    .map((r) => fillRindexFromPlayers(r, item?.players)); // Fill rindex if not set

  // Determine if there are groups with more than the allowed number of players
  const groupCounts = registeredPlayers?.reduce((acc, rp) => {
    if (typeof rp.gindex === 'undefined') return acc;
    if (typeof acc[rp.gindex] === 'undefined') acc[rp.gindex] = 0;
    acc[rp.gindex]++;
    return acc;
  }, {});

  // Count the number of groups that have more than the allowed number of players
  const groupOver = groupCounts
    ? Object.keys(groupCounts).filter((g) => groupCounts[g] > item.ppg)
    : [];

  const okPPG = item?.registered?.length % item?.ppg === 0 ? true : false;
  const isGroupOver = groupOver.length > 0 ? true : false;
  // Check if a group has duplicate roles
  const hasDuplicateRoles = registeredPlayers?.some((rp) => {
    const roleCount = registeredPlayers.filter(
      (r) => r.rindex === rp.rindex && r.gindex === rp.gindex
    )?.length;
    return roleCount > 1;
  });
  const isSaving = item?.status === 'saving' ? true : false;
  const isLaunched = item?.status === 'launched' ? true : false;
  const isDisabled = isLaunched ? true : false;

  return (
    <div className=''>
      <Header />
      <h2 className='d-flex mt-3 sec-title fs-3'>
        {/* Title Left */}
        <span>
          {t('Simulation Setup')}{' '}
          <small
            style={{
              fontSize: '12px',
              padding: '5px 8px',
              verticalAlign: 'middle',
            }}
            className={
              item.status === 'saving'
                ? 'text-capitalize badge badge-pill text-bg-warning'
                : 'text-capitalize badge badge-pill text-bg-success'
            }>
            {item.status === 'saving' ? t('saving...') : t(item.status)}
          </small>
        </span>

        {/* Title Right */}
        <div className='ms-auto' style={{ lineHeight: 1 }}>
          <button
            className='btn btn-md btn-link text-dark me-5'
            onClick={() => setOnboarding(true)}>
            <small className='font-italic'>{t('Need help?')}</small>
          </button>
          <button
            className='btn btn-md btn-outline-primary'
            onClick={submit}
            disabled={!item?.changed && (isDisabled || isSaving)}>
            {t('Save')}
          </button>
          <button
            className='btn btn-md btn-outline-success ms-3'
            onClick={launch}
            disabled={
              isDisabled ||
              isGroupOver ||
              hasDuplicateRoles ||
              isSaving ||
              !okPPG
            }>
            {t('Launch')}
          </button>
        </div>
      </h2>

      <div className='row mt-4'>
        {/* Left Panel: Game Parameters */}
        <div className='col-6'>
          <div className='row'>
            <div className='col-8'>
              <div className='form-group'>
                <label>{t('Game Name')}</label>
                <div className='input-group input-group-lg mt-2'>
                  <input
                    name='name'
                    className='form-control'
                    placeholder={t('Enter game name')}
                    disabled={item.status === 'saving' ? true : false}
                    value={item.name || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            <div className='col-4'>
              <div className='form-group'>
                <label>{t('Access Type')}</label>
                <div className='input-group input-group-lg mt-2'>
                  <select
                    name='access_type'
                    className='form-select'
                    value={item.access_type || ''}
                    onChange={handleChange}
                    disabled={item.status === 'saving' ? true : false}>
                    <option value='csv'>{t('Upload')} Emails</option>
                    <option value='link'>Link</option>
                  </select>
                </div>
              </div>
            </div>

            {/* LINK: Display registration link */}
            {item.access_type === 'link' && (
              <div className='col-12 mt-4'>
                <div className='card'>
                  <div className='card-body bg-dark text-center pt-5 pb-5 pl-5 pr-5'>
                    <code className='text-white' style={{ fontSize: '1.5rem' }}>
                      {item.short_url
                        ? item.short_url
                        : process.env.REACT_APP_GAME_URL + '/join/' + item._id}
                    </code>
                  </div>
                </div>
              </div>
            )}

            {/* CSV: Instructions */}
            {item.access_type === 'csv' && (
              <div className='col-12 mt-4'>
                <div className='card'>
                  <div className='card-body text-left pt-3 pb-3 pl-3 pr-3'>
                    <CSVInstructions />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel: Player Management */}
        <div className='col-6'>
          <div className='d-flex form-group'>
            <label className='d-block w-100 mb-2'>
              {t('Registered Players')}{' '}
              <span>({item.registered ? item.registered.length : 0})</span>
              {item.access_type === 'link' && (
                <small
                  className='btn btn-link btn-sm ms-2 py-0 font-italic'
                  onClick={(e) => {
                    setFetching(true);
                    dispatch(gameActions.fetchGames(item._id));
                    setTimeout(() => setFetching(false), 1000);
                  }}>
                  {fetching ? t('Reloading...') : t('Reload list')}
                </small>
              )}
            </label>

            {/* Error messages */}
            <div className='ms-auto text-end mb-1'>
              {!okPPG && (
                <small
                  style={{
                    fontSize: '12px',
                    padding: '5px 8px',
                    verticalAlign: 'middle',
                  }}
                  className='badge badge-pill bg-warning text-dark'>
                  {/* This game needs {item.ppg} players per group. Add more
                  players. */}
                  {t('Incomplete groups are present.')}
                </small>
              )}

              {isGroupOver && (
                <small
                  style={{
                    fontSize: '12px',
                    padding: '5px 8px',
                    verticalAlign: 'middle',
                  }}
                  className='badge badge-pill bg-danger'>
                  {t(' Groups must have only')} {item.ppg} {t('players')}
                </small>
              )}

              {hasDuplicateRoles && (
                <small
                  style={{
                    fontSize: '12px',
                    padding: '5px 8px',
                    verticalAlign: 'middle',
                  }}
                  className='badge badge-pill bg-danger'>
                  {t('Groups must have')} {item.ppg} roles
                </small>
              )}
            </div>
          </div>

          {/* CSV players setup area */}
          {item.access_type === 'csv' && (
            <CSVDrop onComplete={readCSV}>
              <div className='input-group input-group-lg mt-2 mb-2'>
                <textarea
                  name='players_text'
                  className='form-control'
                  placeholder='email@example.com'
                  value={item.players_text || ''}
                  onChange={handleChange}
                  rows='15'
                  disabled={item.status === 'saving' ? true : false}></textarea>
              </div>
              <small>
                <b>{t('Tip')}</b>:{' '}
                {t('You can also drag and drop your csv file here.')}
              </small>
            </CSVDrop>
          )}

          {/* LINK players setup list */}
          {item.access_type === 'link' && (
            <div className='mb-4'>
              <PlayersManager roleLabels={roleLabels} disabled={isDisabled} />
            </div>
          )}
        </div>
      </div>

      {/* Onboarding Modal */}
      <Modal
        isOpen={onboarding}
        toggle={dississOnboarding}
        centered={true}
        size='lg'>
        <ModalHeader>{t('Onboarding')}</ModalHeader>
        <ModalBody className='text-center'>
          <div className='ratio ratio-16x9'>
            <iframe
              title='Game Setup Tutorial Video'
              src='https://player.vimeo.com/video/383603029'
              width='640'
              height='360'
              frameBorder='0'
              allow='autoplay; fullscreen'
              allowFullScreen></iframe>
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            className='btn btn-outline-primary'
            onClick={dississOnboarding}>
            {t('Continue')}
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default GameForm;
